import { useRef } from 'react';
import { CloudUploadOutlined as UploadIcon } from '@mui/icons-material';
import { Button, Stack, Typography } from '@mui/material';

interface UploadFilesButtonProps {
  /** Receives the files chosen via the picker. The consumer should run
   *  the same validate → upload → trackUpload flow it uses for the
   *  chart-wide drop zone. */
  onFilesSelected: (files: File[]) => void;
  /** Comma-separated `accept` attribute for the file picker. Defaults to
   *  the artifact pipeline's accepted MIME types, so the OS-native
   *  picker greys out unsupported files before the user can pick them. */
  accept?: string;
  /** Inline helper text below the button. Set to `null` to hide. */
  helperText?: string | null;
  size?: 'small' | 'medium';
  label?: string;
  disabled?: boolean;
  /** Stretch the button to fill its container — set this when slotting
   *  into a narrow vertical column (e.g. the patient documents block)
   *  so the button doesn't right-align against an empty edge. */
  fullWidth?: boolean;
  /** Cross-axis alignment of the button + helper text. Defaults to
   *  `right` since this typically lives in the right-edge action slot
   *  of a header. Set to `left` when placed in a body / left-aligned
   *  container. Ignored when `fullWidth` is true. */
  align?: 'left' | 'right';
}

/**
 * Affordance for users who don't realize the page is a drop zone (or
 * are on a device where drag-and-drop is awkward). Triggers a hidden
 * file input and forwards the chosen files to the same handler the
 * drop zone uses, so validation / ghost UX / dedup all behave
 * identically across both entry points.
 */
export function UploadFilesButton({
  onFilesSelected,
  accept = 'application/pdf,image/jpeg,image/png,image/gif,image/webp',
  helperText = 'drag and drop',
  size = 'small',
  label = 'Upload files',
  disabled = false,
  fullWidth = false,
  align = 'right',
}: UploadFilesButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const stackAlign = fullWidth
    ? 'stretch'
    : align === 'left'
      ? 'flex-start'
      : 'flex-end';
  const helperAlign = fullWidth ? 'center' : align;

  return (
    <Stack
      spacing={0.25}
      sx={[{ alignItems: stackAlign }, ...(fullWidth ? [{ width: '100%' }] : [])]}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        style={{ display: 'none' }}
        onChange={e => {
          const files = Array.from(e.target.files ?? []);
          if (files.length > 0) onFilesSelected(files);
          // Reset so the same file can be picked again immediately if
          // the previous attempt was rejected (e.g. duplicate).
          if (inputRef.current) inputRef.current.value = '';
        }}
        aria-label={label}
      />
      <Button
        size={size}
        variant="outlined"
        fullWidth={fullWidth}
        startIcon={<UploadIcon />}
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        sx={{ textTransform: 'none', fontWeight: 600 }}
      >
        {label}
      </Button>
      {helperText && (
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ fontStyle: 'italic', textAlign: helperAlign }}
        >
          {helperText}
        </Typography>
      )}
    </Stack>
  );
}
